/**
 * VoiceNoteRecorder — enregistreur de note vocale style WhatsApp.
 *
 * UX :
 * - Appuyez et MAINTENEZ le bouton micro pour enregistrer ; relâchez pour arrêter.
 * - Au clavier : Entrée/Espace démarre puis arrête (bascule), pour l'accessibilité.
 * - Si plusieurs micros : sélecteur de périphérique audio.
 * - Après l'enregistrement : écoute (review), puis suppression / réenregistrement.
 * - Arrêt automatique à la durée maximale.
 *
 * Encodage standardisé via `utils/audioRecording.js` (Opus/WebM ~24 kbps, repli MP4 iOS).
 *
 * Props :
 * - onChange({ blob, mime, durationS } | null) : appelé quand une note est prête ou supprimée.
 * - maxDurationS : durée max (défaut 120 s).
 * - disabled : désactive l'enregistreur.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  isRecordingSupported,
  buildRecorderOptions,
  pickSupportedMimeType,
  VOICE_AUDIO_CONSTRAINTS,
  VOICE_MAX_DURATION_S,
  formatDuration,
} from '../../utils/audioRecording.js';
import './VoiceNoteRecorder.css';

function VoiceNoteRecorder({ onChange, maxDurationS = VOICE_MAX_DURATION_S, disabled = false }) {
  const selectId = useId();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [durationS, setDurationS] = useState(0);
  const [permissionError, setPermissionError] = useState('');
  const [mics, setMics] = useState([]);
  const [micId, setMicId] = useState('');

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const timerRef = useRef(null);
  const audioUrlRef = useRef(null);
  const micIdRef = useRef('');
  const supported = isRecordingSupported();

  useEffect(() => {
    micIdRef.current = micId;
  }, [micId]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const revokeUrl = useCallback(() => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const refreshMics = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((d) => d.kind === 'audioinput' && d.deviceId);
      setMics(inputs);
      setMicId((prev) => {
        if (prev && inputs.some((d) => d.deviceId === prev)) return prev;
        return inputs[0]?.deviceId || '';
      });
    } catch {
      /* ignore — select stays hidden */
    }
  }, []);

  // Initial list (labels often empty until permission); refresh after permission.
  useEffect(() => {
    if (!supported) return undefined;
    refreshMics();
    const onDeviceChange = () => refreshMics();
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
    };
  }, [supported, refreshMics]);

  useEffect(() => {
    return () => {
      clearTimer();
      stopStream();
      revokeUrl();
      if (recorderRef.current?.state === 'recording') {
        try {
          recorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
    };
  }, [clearTimer, stopStream, revokeUrl]);

  const stopRecording = useCallback(() => {
    clearTimer();
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
    setRecording(false);
  }, [clearTimer]);

  const startRecording = useCallback(async () => {
    if (disabled || recording || !supported) return;
    setPermissionError('');
    revokeUrl();
    setAudioUrl(null);
    setDurationS(0);
    onChange?.(null);

    const buildConstraints = (deviceId) => {
      const audio = { ...VOICE_AUDIO_CONSTRAINTS };
      // `exact` so getUserMedia cannot silently pick another mic
      if (deviceId) audio.deviceId = { exact: deviceId };
      return { audio };
    };

    const isUnavailableDeviceError = (err) => {
      const name = err?.name || '';
      return (
        name === 'OverconstrainedError' ||
        name === 'NotFoundError' ||
        name === 'DevicesNotFoundError'
      );
    };

    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(buildConstraints(micIdRef.current));
      } catch (err) {
        // Only fall back when the chosen device is gone/unusable — not on permission denials
        if (micIdRef.current && isUnavailableDeviceError(err)) {
          stream = await navigator.mediaDevices.getUserMedia({ audio: VOICE_AUDIO_CONSTRAINTS });
        } else {
          throw err;
        }
      }
      streamRef.current = stream;
      await refreshMics();

      const options = buildRecorderOptions();
      let recorder;
      try {
        recorder = new MediaRecorder(stream, options);
      } catch {
        recorder = new MediaRecorder(stream);
      }
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const mime = recorder.mimeType || pickSupportedMimeType() || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        stopStream();
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          audioUrlRef.current = url;
          setAudioUrl(url);
          const secs = Math.max(1, Math.round((Date.now() - startTsRef.current) / 1000));
          setDurationS(secs);
          onChange?.({ blob, mime, durationS: secs });
        }
      };
      startTsRef.current = Date.now();
      recorder.start(200);
      recorderRef.current = recorder;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        const secs = (Date.now() - startTsRef.current) / 1000;
        setElapsed(secs);
        if (secs >= maxDurationS) {
          stopRecording();
        }
      }, 200);
    } catch {
      setPermissionError(
        'Micro indisponible. Autorisez le microphone dans votre navigateur, ou utilisez le champ texte.'
      );
      setRecording(false);
      stopStream();
    }
  }, [
    disabled,
    recording,
    supported,
    revokeUrl,
    onChange,
    maxDurationS,
    stopRecording,
    stopStream,
    refreshMics,
  ]);

  const handleDelete = useCallback(() => {
    revokeUrl();
    setAudioUrl(null);
    setDurationS(0);
    setElapsed(0);
    onChange?.(null);
  }, [revokeUrl, onChange]);

  const handleKeyToggle = useCallback(
    (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      if (recording) stopRecording();
      else startRecording();
    },
    [recording, startRecording, stopRecording]
  );

  if (!supported) {
    return (
      <div className="vnr" role="note">
        <p className="vnr-unsupported">
          L&apos;enregistrement vocal n&apos;est pas disponible sur ce navigateur. Utilisez le champ
          texte.
        </p>
      </div>
    );
  }

  const showMicSelect = mics.length > 1 && !audioUrl;

  return (
    <div className="vnr">
      {showMicSelect && (
        <div className="vnr-mic-select-row">
          <label htmlFor={selectId} className="vnr-mic-select-label">
            Microphone
          </label>
          <select
            id={selectId}
            className="vnr-mic-select"
            value={micId}
            disabled={disabled || recording}
            onChange={(e) => setMicId(e.target.value)}
          >
            {mics.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Micro ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {!audioUrl && (
        <div className="vnr-record-row">
          <button
            type="button"
            className={`vnr-mic ${recording ? 'recording' : ''}`}
            onPointerDown={(e) => {
              e.preventDefault();
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch {
                /* ignore */
              }
              startRecording();
            }}
            onPointerUp={(e) => {
              e.preventDefault();
              try {
                e.currentTarget.releasePointerCapture(e.pointerId);
              } catch {
                /* ignore */
              }
              if (recording) stopRecording();
            }}
            onPointerCancel={() => {
              if (recording) stopRecording();
            }}
            onKeyDown={handleKeyToggle}
            disabled={disabled}
            aria-pressed={recording}
            aria-label={
              recording
                ? "Relâchez pour arrêter l'enregistrement"
                : 'Maintenir pour enregistrer un message vocal'
            }
          >
            <span className="vnr-mic-icon" aria-hidden="true">
              🎤
            </span>
          </button>
          <div className="vnr-record-status" aria-live="polite">
            {recording ? (
              <>
                <span className="vnr-rec-dot" aria-hidden="true" />
                <span className="vnr-timer">{formatDuration(elapsed)}</span>
                <span className="vnr-hint">
                  Relâchez pour arrêter (max {formatDuration(maxDurationS)})
                </span>
              </>
            ) : (
              <span className="vnr-hint">
                Maintenez le micro pour enregistrer, relâchez pour arrêter
              </span>
            )}
          </div>
        </div>
      )}

      {audioUrl && (
        <div className="vnr-review">
          <audio controls src={audioUrl} className="vnr-audio">
            Votre navigateur ne supporte pas la lecture audio.
          </audio>
          <div className="vnr-review-meta">
            <span className="vnr-duration">Durée : {formatDuration(durationS)}</span>
            <button
              type="button"
              className="btn-link vnr-delete"
              onClick={handleDelete}
              disabled={disabled}
            >
              🗑️ Supprimer et réenregistrer
            </button>
          </div>
        </div>
      )}

      {permissionError && (
        <p className="vnr-error" role="alert">
          {permissionError}
        </p>
      )}
    </div>
  );
}

export default VoiceNoteRecorder;
